import { getAxiosInstance } from "../utils/apiClient";
import { getTokens } from "../utils/tokens";
import {
  getUserPersonalInfo,
  getUserProfile,
  getNotificationSettingsAPI,
  getMySubscriptionPlansAPI,
  getCreatorSubscriptionPlansAPI,
} from "../api/userApi";

/** Відповідь /api/monetization/credits */
export interface CreditsResponse {
  credits?: number;
  totalCredits: number;
  usedCredits: number;
  subscriptionTier?: string;
  lastUpdated?: string;
  subscription?: {
    tier: string;
    status: string;
    expiresAt?: string;
    autoRenew?: boolean;
    creditsUsed?: number;
    creditsIncluded?: number;
  };
}

async function fetchCredits(): Promise<CreditsResponse> {
  const tokens = getTokens();
  if (!tokens?.access_token) {
    throw new Error("Missing auth tokens");
  }
  const response = await getAxiosInstance().get<{ success: boolean; data: CreditsResponse }>(
    "/api/monetization/credits",
    {
      headers: {
        "Content-Type": "application/json",
        [process.env.REACT_APP_AUTH_TOKEN as string]: `Bearer ${tokens.access_token}`,
        [process.env.REACT_APP_FIREBASE_AUTH_TOKEN as string]: `Bearer ${tokens.firebase_token}`,
        "ngrok-skip-browser-warning": "true",
      },
    }
  );
  if (!response.data.success || !response.data.data) {
    throw new Error((response.data as any)?.message || "Failed to fetch credits");
  }
  return response.data.data;
}

/** Персональна інформація: /api/user/personal-info */
export async function fetchPersonalInfo(): Promise<any> {
  const res = await getUserPersonalInfo();
  if (res?.success && res?.data) return res.data;
  throw new Error(res?.message || "Failed to fetch personal info");
}

/** Профіль юзера: /api/user/profile (id або username) */
export async function fetchUserProfile(idOrUsername: string): Promise<any> {
  const res = await getUserProfile(idOrUsername);
  if (res?.data) return res.data;
  throw new Error(res?.message || "Failed to fetch profile");
}

/** Налаштування сповіщень: /api/user/notifications */
export async function fetchNotificationSettings(): Promise<any> {
  const res = await getNotificationSettingsAPI();
  if (res) return res;
  throw new Error("Failed to fetch notification settings");
}

/** Мої плани підписки: /api/user/profile/subscription-plans */
export async function fetchMySubscriptionPlans(): Promise<{
  items: any[];
  meta?: { freeEnabled?: boolean; trialEnabled?: boolean };
}> {
  const data = await getMySubscriptionPlansAPI();
  const list = Array.isArray(data) ? data : data?.plans ?? data?.items ?? [];
  return {
    items: Array.isArray(list) ? list : [],
    meta: (data as any)?.meta,
  };
}

/** Плани конкретного кріейтора: /api/user/profile/:userId/subscription-plans */
export async function fetchCreatorSubscriptionPlans(
  userId: string,
): Promise<{
  items: any[];
  meta?: { freeEnabled?: boolean; trialEnabled?: boolean };
}> {
  const data = await getCreatorSubscriptionPlansAPI(userId);
  const list = Array.isArray(data)
    ? data
    : data?.plans ?? data?.items ?? [];
  return {
    items: Array.isArray(list) ? list : [],
    meta: (data as any)?.meta,
  };
}

export const queryApi = {
  fetchCredits,
  fetchPersonalInfo,
  fetchUserProfile,
  fetchNotificationSettings,
  fetchMySubscriptionPlans,
  fetchCreatorSubscriptionPlans,
};
